import anime from 'animejs';
import Scene from '../classes/scene';
import SceneManager from '../classes/scene-manager';
import {getEl, getEls, isGreaterThanOrEqualTo} from '../helpers';
import {ScrollAnimation} from "../components/scrolllocked-animations";
import ResponsiveWatcher, {ResponsiveSize} from "../classes/responsive-watcher";
import Globals from "../classes/globals";

export default class SidewaysScroller extends Scene {

	easeAnimation: ScrollAnimation;
	sidewaysScroller: ScrollAnimation;

	constructor(manager: SceneManager, targetContainer: string) {
		super(manager, targetContainer);

		const bullets = getEls('.sws-bullets .sws-bullet');

		const scroller = this.elContainer.querySelector('.sws-scroller');

		if (!scroller) return;

		let activeBullet = 0;

		this.easeAnimation = this.addScrollAnimation({
			targets: scroller,
			duration: 1000,
			delay: 0,
			translateX: ['0%', '-100%'],
			easing: 'easeInOutSine',
			update: anim => {

				const progress = anim.progress / 100;
				if (bullets.length) {
					const newBullet = Math.min(Math.round(progress * (bullets.length - 1)), bullets.length - 1);

					if (newBullet != activeBullet) {
						bullets[activeBullet].classList.remove('active');
						bullets[newBullet].classList.add('active');
						activeBullet = newBullet;
					}
				}
			},
		}, this.elContainer.querySelector('.sws-container'), 100, 0, 1, -1.5, -0.8, 0.5);

		this.sidewaysScroller = this.addScrollAnimation({
			targets: this.elContainer.querySelector('.sws-sticky'),
			duration: 1000,
			top: [
				{ value: '30vh', duration: 0 },
				{ value: '0vh', duration: 300 },
				{ value: '0vh', duration: 300 },
				{ value: '-150vh', duration: 200, easing: 'easeInSine' },
			],
			paddingTop: [
				{ value: '100px', duration: 0  },
			],
			easing: 'easeOutSine',
		}, this.elContainer.querySelector('.sws-container'), 50, 0, 1, 0, 0.6, 0.7);


		const methaneModalMap: { [key: string]: string } = {
			'.sws-spotlight-1':  'methane',
			'.sws-spotlight-1b': 'carbon_capture',
			'.sws-spotlight-1c': 'wireline',
			'.sws-spotlight-1d': 'scaling'
		};

		Object.keys(methaneModalMap).forEach(cls => {
			const spotlight = this.elContainer.querySelector(cls);
			if (!spotlight) return;

			const mols = [
				{
					el: spotlight.querySelector('.mol-large'),
					dx: 0,
					dy: 0,
					amount: -2,
				},
				{
					el: spotlight.querySelector('.mol-small-1'),
					dx: 0,
					dy: 0,
					amount: -4,
				},
			];

			manager.app.spotlighter.addSpotlightAnimation(
				{
					targets: spotlight.querySelectorAll('.mol'),
					opacity: [0, 1],
					duration: 1500,
					easing: "easeOutCubic",
				},
				spotlight as HTMLElement,
				200,
				(anim, delta) => {
					const xRatio = (anim.mouseX / anim.elWidth) - 0.5;
					const yRatio = (anim.mouseY / anim.elHeight) - 0.5;

					for (let i = 0; i < mols.length; i++) {
						if (!mols[i].el) continue;
						const targetDx = xRatio * mols[i].amount;
						const targetDy = yRatio * mols[i].amount;
						mols[i].dx += (targetDx - mols[i].dx) * delta * anim.smoothSpeed;
						mols[i].dy += (targetDy - mols[i].dy) * delta * anim.smoothSpeed;

						// @ts-ignore
						mols[i].el.style.transform = 'translate(' + mols[i].dx + 'vw, ' + mols[i].dy + 'vw)';
					}
				},
				() => {
					const heading = spotlight.querySelector(".spot-heading");
					if (heading) heading.dispatchEvent(new CustomEvent('play'));
				}
			);

			spotlight.addEventListener('click', (e) => {
				e.preventDefault();
				const modalName = methaneModalMap[cls];
				manager.app.modaller.openModal(modalName);
			});
		});

		//SPOTLIGHT 2 ANIMATION
		const spotlight2 = this.elContainer.querySelector('.sws-spotlight-2');
		if (spotlight2) {
			manager.app.spotlighter.addSpotlightAnimation(
				{
					targets: spotlight2.querySelectorAll('.spot-heading div'),
					fontSize: (el, i, len) => ['max(10.1rem, 22vh)', 'max(5.6rem, 10vh)', 'max(2.9rem, 7vh)'][i],
					delay: anime.stagger(250),
					duration: 2000,
					easing: "easeOutQuart",
				},
				spotlight2 as HTMLElement,
				200,
				null,
				() => {
					const heading = spotlight2.querySelector(".spot-heading");
					if (heading) heading.dispatchEvent(new CustomEvent('play'));
				},
				true
			);
			spotlight2.addEventListener('click', (e) => {
				e.preventDefault();
				manager.app.modaller.openModal('concrete');
			});
		}


		//SPOTLIGHT 3 ANIMATION
		const spotlight3 = this.elContainer.querySelector('.sws-spotlight-3');
		if (spotlight3) {
			const wireline = [
				{
					el: spotlight3.querySelector('.spot-img'),
					dx: 0,
					dy: 0,
					amount: -2,
				},
			]

			manager.app.spotlighter.addSpotlightAnimation(
				{
					targets: spotlight3.querySelectorAll('.spot-img'),
					top: ['100%', '39%'],
					duration: 2000,
					easing: "easeOutQuart",
				},
				spotlight3 as HTMLElement,
				200,
				(anim, delta) => {
					const xRatio = (anim.mouseX / anim.elWidth) - 0.5;
					const yRatio = (anim.mouseY / anim.elHeight) - 0.5;

					for (let i = 0; i < wireline.length; i++) {
						if (!wireline[i].el) continue;
						const targetDx = xRatio * wireline[i].amount;
						const targetDy = yRatio * wireline[i].amount;
						wireline[i].dx += (targetDx - wireline[i].dx) * delta * anim.smoothSpeed;
						wireline[i].dy += (targetDy - wireline[i].dy) * delta * anim.smoothSpeed;

						// @ts-ignore
						wireline[i].el.style.transform = 'translate(calc(' + wireline[i].dx + 'vw - 50%), ' + wireline[i].dy + 'vw)';
					}
				},
				() => {
					const heading = spotlight3.querySelector(".spot-heading");
					if (heading) heading.dispatchEvent(new CustomEvent('play'));
				},
				true
			);
			spotlight3.addEventListener('click', (e) => {
				e.preventDefault();
				manager.app.modaller.openModal('wireline');
			});
		}

		// SPOTLIGHT 4 ANIMATION:
		const spotlight4 = this.elContainer.querySelector('.sws-spotlight-4');
		if (spotlight4) {
			const spotvals4 = [
				{
					el: spotlight4.querySelector('.spot-image-bottom img'),
					dx: 0,
					dy: 0,
					amount: -1,
				},
			]

			manager.app.spotlighter.addSpotlightAnimation(
				{
					targets: spotlight4.querySelector('.zoomer'),
					scale: [1, 1.1],
					duration: 2000,
					easing: "easeOutQuart",
				},
				spotlight4 as HTMLElement,
				200,
				(anim, delta) => {
					const xRatio = (anim.mouseX / anim.elWidth) - 0.5;
					const yRatio = (anim.mouseY / anim.elHeight) - 0.5;

					for (let i = 0; i < spotvals4.length; i++) {
						if (!spotvals4[i].el) continue;
						const targetDx = xRatio * spotvals4[i].amount;
						const targetDy = yRatio * spotvals4[i].amount;
						spotvals4[i].dx += (targetDx - spotvals4[i].dx) * delta * anim.smoothSpeed;
						spotvals4[i].dy += (targetDy - spotvals4[i].dy) * delta * anim.smoothSpeed;

						// @ts-ignore
						spotvals4[i].el.style.transform = 'translate(' + spotvals4[i].dx + 'vw, ' + spotvals4[i].dy + 'vw)';
					}
				},
				() => {
					const heading = spotlight4.querySelector(".spot-heading");
					if (heading) heading.dispatchEvent(new CustomEvent('play'));
				},
				true
			);
			spotlight4.addEventListener('click', (e) => {
				e.preventDefault();
				manager.app.modaller.openModal('carbon_capture');
			});
		}

		// SPOTLIGHT 5 ANIMATION:
		const spotlight5 = this.elContainer.querySelector('.sws-spotlight-5');
		if (spotlight5) {
			const spotvals5 = [
				{
					el: spotlight5.querySelector('.spot-image-bottom img'),
					dx: 0,
					dy: 0,
					amount: -1,
				},
			]

			manager.app.spotlighter.addSpotlightAnimation(
				{
					targets: spotlight5.querySelector('.zoomer'),
					scale: [1, 1.1],
					duration: 2000,
					easing: "easeOutQuart",
				},
				spotlight5 as HTMLElement,
				200,
				(anim, delta) => {
					const xRatio = (anim.mouseX / anim.elWidth) - 0.5;
					const yRatio = (anim.mouseY / anim.elHeight) - 0.5;

					for (let i = 0; i < spotvals5.length; i++) {
						if (!spotvals5[i].el) continue;
						const targetDx = xRatio * spotvals5[i].amount;
						const targetDy = yRatio * spotvals5[i].amount;
						spotvals5[i].dx += (targetDx - spotvals5[i].dx) * delta * anim.smoothSpeed;
						spotvals5[i].dy += (targetDy - spotvals5[i].dy) * delta * anim.smoothSpeed;

						// @ts-ignore
						spotvals5[i].el.style.transform = 'translate(' + spotvals5[i].dx + 'vw, ' + spotvals5[i].dy + 'vw)';
					}
				},
				() => {
					const heading = spotlight5.querySelector(".spot-heading");
					if (heading) heading.dispatchEvent(new CustomEvent('play'));
				},
				true
			);
			spotlight5.addEventListener('click', (e) => {
				e.preventDefault();
				manager.app.modaller.openModal('lithium');
			});
		}


	}

	onResponsiveChange(newSize: ResponsiveSize, oldSize: ResponsiveSize,responser: ResponsiveWatcher) {

		if (isGreaterThanOrEqualTo(newSize, ResponsiveSize.lg))
		{
			this.manager.app.scrolllocker.enableAnimation(this.sidewaysScroller);
			this.manager.app.scrolllocker.enableAnimation(this.easeAnimation);
		}
		else {
			this.manager.app.scrolllocker.disableAnimation(this.sidewaysScroller);
			this.manager.app.scrolllocker.disableAnimation(this.easeAnimation);

		}
	}

}
